
import { Component, Vue, Watch } from 'vue-property-decorator';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import Toasted from 'vue-toasted';
import { exposeHeaders, handleError, initClient } from '../../../api';
import { emptyUUID, formatDateTime } from '../../../util';
import {
  GroupSurveyExtension,
  GroupSurveyExtensionUiClient,
  GroupSurveyUserPresence,
  GroupUiClient,
  ListItem,
  SettingsApiControllerUIClient,
  VisitorManagementUiClient,
  VmCockpitRow,
} from '../../backend/backendapi';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import Spinner from '../../util/Spinner.vue';
import PrintBadgeButton from '../group/PrintBadgeButton.vue';
import CenterOnParent from '../../util/CenterOnParent.vue';
import PresenceStartStopButton from '../advanceRegistration/PresenceStartStopButton.vue';
import GroupSurveyExtensionStore from '../../../stores/GroupSurveyExtensionStore';
import GroupsForSurveyEditModal from '../group/GroupsForSurveyEditModal.vue';
import DirectRegistrationModal from './DirectRegistrationModal.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import { dateRangePickerLocale } from '../../../util/datepicker';
import CockpitSignalRHandler from './TabCockpit/CockpitSignalRHandler';
import { Results, VgtParameters, VgtRemoteManger } from '../../../util/VgtRemoteManger';
import Visible from '../../../directives/visible';
import PictureModal from './TabCockpit/PictureModal.vue';

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    PictureModal,
    CenterOnParent,
    DateRangePicker,
    DirectRegistrationModal,
    GroupsForSurveyEditModal,
    PresenceStartStopButton,
    PrintBadgeButton,
    Spinner,
    VueGoodTable,
  },
  directives: {
    Visible,
  },
})
export default class TabCockpit extends Vue {
  private readonly visitorApi = exposeHeaders(initClient(VisitorManagementUiClient), 'getCockpit');
  private readonly groupApi = initClient(GroupUiClient);
  private readonly groupSurveyExtensionApi = initClient(GroupSurveyExtensionUiClient);
  private readonly settingsApi = initClient(SettingsApiControllerUIClient);

  readonly store = new GroupSurveyExtensionStore();
  readonly vgtManager = new VgtRemoteManger(this.loadCockpit.bind(this), 'en', [
    { field: 'visitorGroupNamesAsString', type: 'string' },
    { field: 'fullName', type: 'string' },
    { field: 'company', type: 'string' },
    { field: 'responsibleContactFullName', type: 'string' },
    { field: 'approvalStateText', type: 'string' },
    { field: 'presenceStatusText', type: 'string' },
  ]);

  formatDateTime = formatDateTime;
  loading: boolean = false;
  from = new Date();
  to = new Date();
  readonly users: ListItem[] = [];
  task: any = null;
  hiddenColumns: string[] = [];
  private fromToUpdatedDebounceTask: any = null;
  private cockpitSignalR = new CockpitSignalRHandler(this.from, this.to, new Date());
  private statsUpdateDateUpdateTask: any = null;

  get availableSalutations(): string[] {
    return [...new Set([...this.store.groupSurveyExtensions.filter(e => e.group.feature === 'survey')]
        .flatMap(group => group.group.externalUsers ?? [])
        .map(e => e.salutation)
        .filter(e => e?.trim().length)
        .map(e => e!),
    )];
  }

  get checkedInCount(): number {
    return this.cockpitSignalR.statsPastMonths.checkedInCount;
  }

  get cockpit(): VmCockpitRow[] {
    return this.vgtManager.results.data;
  }

  get columnsConfig(): any[] {
    return [{
      label: this.$t('visitorManagement.cockpit.model.visitorGroupNames'),
      field: 'visitorGroupNamesAsString',
      hidden: this.hiddenColumns.includes('visitorGroupNames'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.startdate'), //TODO fix typo startdate => startDate
      field: 'startDate',
      type: 'date',
      formatFn: (d: Date | undefined) => !d ? '' : formatDateTime(d),
      isStartDate: true,
      hidden: this.hiddenColumns.includes('startDate'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.endDate'),
      field: 'endDate',
      type: 'date',
      formatFn: (d: Date | undefined) => !d ? '' : formatDateTime(d),
      hidden: this.hiddenColumns.includes('endDate'),
    }, {
      label: this.$t('visitorManagement.cockpit.visitorName'),
      field: 'fullName',
      hidden: this.hiddenColumns.includes('visitorName'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.company'),
      field: 'company',
      hidden: this.hiddenColumns.includes('company'),
    }, {
      label: this.$t('visitorManagement.cockpit.responsibleContact'),
      field: 'responsibleContactFullName',
      hidden: this.hiddenColumns.includes('responsibleContact'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.approvalState'),
      field: 'approvalStateText',
      hidden: this.hiddenColumns.includes('approvalState'),
    }, {
      label: this.$t('visitorManagement.cockpit.presence'),
      field: 'presenceStatusText',
      hidden: this.hiddenColumns.includes('presence'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.surveysCompleted'),
      field: 'surveysCompleted',
      type: 'boolean',
      hidden: this.hiddenColumns.includes('surveysCompleted'),
    }, {
      label: this.$t('visitorManagement.cockpit.model.instructionInPerson'),
      field: 'instructionInPerson',
      type: 'boolean',
      hidden: this.hiddenColumns.includes('instructionInPerson'),
    }, {
      label: this.$t('general.actions'),
      field: e => 1, //dummy
      isActions: true,
      sortable: false,
    }];
  }

  get cockpitReferencedGroups(): GroupSurveyExtension[] {
    const groupIds = new Set(this.cockpit.map(e => e.groupSurveyExtensionId));
    return this.store.groupSurveyExtensions.filter(e => groupIds.has(e.id));
  }

  get dateRange(): { startDate: Date; endDate: Date } {
    return {
      startDate: this.from,
      endDate: this.to,
    };
  }

  set dateRange(val: { startDate: Date; endDate: Date }) {
    if (val && val.startDate && val.endDate) {
      let startDate = new Date(val.startDate);
      let endDate = new Date(val.endDate);

      // Guard: Ensure range does not exceed 3 month (92 days max limit)
      const maxAllowedEndDate = new Date(startDate);
      maxAllowedEndDate.setMonth(maxAllowedEndDate.getMonth() + 3);

      if (endDate > maxAllowedEndDate) {
        endDate = maxAllowedEndDate;
      }

      this.from = startDate;
      this.to = endDate;
    }
  }

  get dateRangePickerLocale(): unknown {
    return dateRangePickerLocale(this.$i18n);
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get sortOptions(): any {
    return {
      enabled: true,
      initialSortBy: {
        field: this.columnsConfig.find(e => e.isStartDate).field,
        type: 'desc',
      },
    };
  }

  get totalRows(): number {
    return this.vgtManager.results.totalRows;
  }

  get visitorsToday(): number {
    return this.cockpitSignalR.statsToday.totalCount;
  }

  mounted(): void {
    this.from = new Date();
    this.from.setHours(0, 0, 0, 0); //midnight
    this.to = new Date(this.from);
    this.to.setDate(this.to.getDate() + 1); //midnight +1d
    this.reload();

    this.cockpitSignalR.onUpdateCockpit(() => this.vgtManager.reload());
    this.cockpitSignalR.updateDateRange(this.from, this.to);
    this.cockpitSignalR.updateStatsDate(new Date());
    this.cockpitSignalR.start();
    this.statsUpdateDateUpdateTask = setInterval(() => this.cockpitSignalR.updateStatsDate(new Date()), 5000);
  }

  beforeDestroy(): void {
    clearInterval(this.statsUpdateDateUpdateTask);
    this.cockpitSignalR.stop();
  }

  private async loadCockpit(params: VgtParameters): Promise<Results<VmCockpitRow>> {
    const response = await this.visitorApi.getCockpit(this.from, this.to, params.filter, params.sort, params.page, params.pageSize);
    return { data: response.content, totalRows: parseInt(response.headers.get('total') ?? '0') };
  }

  async openGroupDetailsModal(id: string): Promise<void> {
    try {
      const group = await this.groupSurveyExtensionApi.getById(id);
      (this.$refs.detailsModal as GroupsForSurveyEditModal).open(group);
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  async openGroupUserPictureModal(id: string, externalUserId: string): Promise<void> {
    try {
      const group = await this.groupSurveyExtensionApi.getById(id);
      const externalUser = group.group.externalUsers.find((x) => x.id === externalUserId);
      if (!externalUser || !externalUser.mediaId) {
        this.$toasted.error(this.$t('visitorManagement.cockpit.noExternalUserPicture').toString());
        return;
      }
      (this.$refs.pictureModal as PictureModal).open(externalUser);
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  async reload(): Promise<void> {
    this.loading = true;
    try {
      await this.store.reload();
      await this.reloadUsers();
      await this.reloadSetting();
    } finally {
      this.loading = false;
    }
  }

  async reloadSetting(): Promise<void> {
    try {
      const hiddenColumns = await this.settingsApi.getSystemSetting('VisitorManagementSettings.CockpitHiddenColumns');
      if (Array.isArray(hiddenColumns.result!)) {
        this.hiddenColumns = hiddenColumns.result;
      } else {
        this.hiddenColumns = [];
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
      this.hiddenColumns = [];
    }
  }

  private async reloadUsers(): Promise<void> {
    const list = await this.groupApi.listUsers();
    this.users.splice(0);
    this.users.push(...list);
  }

  onPresenceChanged(presence: GroupSurveyUserPresence): void {
    const group = this.store.groupSurveyExtensionsById.get(presence.groupSurveyExtensionId);
    if (!group) {
      return;
    }

    const cockpit = this.cockpit.find(e => e.groupSurveyExtensionId === presence.groupSurveyExtensionId
        && e.userId === presence.userId
        && e.externalUserId === presence.groupUserExternalId);
    if (cockpit) {
      cockpit.presenceId = presence.id;
      cockpit.checkedIn = presence.startAt;
      cockpit.checkedOut = presence.endAt;
    }
  }

  async saveGroup(group: GroupSurveyExtension): Promise<GroupSurveyExtension> {
    let res: GroupSurveyExtension;
    if (!group.id || group.id === emptyUUID()) {
      res = await this.groupSurveyExtensionApi.create(group);
    } else {
      res = await this.groupSurveyExtensionApi.update(group);
    }
    this.store.rememberGroupSurveyExtension(res);
    return res;
  }

  selectLast3Months(): void {
    let to = new Date();
    to.setHours(0, 0, 0, 0); //midnight
    to.setDate(to.getDate() + 1); //midnight +1d

    let from = new Date(to);
    from.setMonth(from.getMonth() - 3);

    this.from = from;
    this.to = to;
  }

  // selectToday(): void {
  //   let from = new Date();
  //   from.setHours(0, 0, 0, 0); //midnight
  //   let to = new Date(from);
  //   to.setDate(this.to.getDate() + 1); //midnight +1d
  //
  //   this.from = from;
  //   this.to = to;
  //   this.reloadCockpit();
  // }

  @Watch('from')
  @Watch('to')
  onDateRangeUpdated(): void {
    clearTimeout(this.fromToUpdatedDebounceTask);
    this.fromToUpdatedDebounceTask = setTimeout(() => {
      this.loading = true;
      this.cockpitSignalR.updateDateRange(this.from, this.to);
    }, 100);
  }

  @Watch('cockpit')
  onCockpitUpdated(): void {
    this.loading = false;
  }
}
